/**
 * The menu, and the reference lists every screen needs.
 *
 * Both are loaded once after sign-in and kept in memory. The POS grid then
 * filters and searches locally, so tapping between categories and typing in the
 * search box are instant - which is the difference between a till that feels
 * like a till and one that feels like a website.
 *
 * The menu is refetched when it is edited, which is rare.
 */

import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { menuApi, lookupApi } from '../services/endpoints';

export const loadMenu = createAsyncThunk('menu/load', async () => menuApi.getPosMenu());
export const loadLookups = createAsyncThunk('menu/lookups', async () => lookupApi.getAll());

const initialState = {
    categories: [],
    items: [],
    lookups: {
        provinces: [], districts: [], uoms: [], hsCodes: [], saleTypes: [], taxRates: [],
        orderTypes: [], orderStatuses: [], paymentModes: [], discountTypes: [],
        kitchenSections: [], voidReasons: [], wastageReasons: [], itemTypes: [], designations: []
    },
    settings: {},
    business: null,
    outlets: [],
    receiptTemplate: null,

    status: 'idle',
    lookupStatus: 'idle',
    error: null,

    // POS grid state
    activeCategoryId: null,   // null = All Items
    search: '',
    viewMode: 'grid'          // grid | list, the two buttons beside the search box
};

const menuSlice = createSlice({
    name: 'menu',
    initialState,
    reducers: {
        setActiveCategory: (state, action) => { state.activeCategoryId = action.payload; },
        setSearch: (state, action) => { state.search = action.payload; },
        setViewMode: (state, action) => { state.viewMode = action.payload; },

        /**
         * Flip an item's availability locally, so the tile greys out on the tap
         * rather than after a round trip. The server call runs alongside; a
         * failure refetches the menu.
         */
        setItemAvailability: (state, action) => {
            const { menuItemId, isAvailable } = action.payload;
            const item = state.items.find((i) => i.menuItemId === menuItemId);
            if (item) item.isAvailable = isAvailable;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadMenu.pending, (state) => { state.status = 'loading'; })
            .addCase(loadMenu.fulfilled, (state, action) => {
                state.categories = action.payload.categories;
                state.items = action.payload.items;
                state.status = 'ready';
                state.error = null;
            })
            .addCase(loadMenu.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message;
            })

            .addCase(loadLookups.pending, (state) => { state.lookupStatus = 'loading'; })
            .addCase(loadLookups.fulfilled, (state, action) => {
                const { settings, business, outlets, receiptTemplate, ...lookups } = action.payload;
                state.lookups = { ...state.lookups, ...lookups };
                state.settings = settings || {};
                state.business = business || null;
                state.outlets = outlets || [];
                state.receiptTemplate = receiptTemplate || null;
                state.lookupStatus = 'ready';
            })
            .addCase(loadLookups.rejected, (state, action) => {
                state.lookupStatus = 'error';
                state.error = action.error.message;
            });
    }
});

export const { setActiveCategory, setSearch, setViewMode, setItemAvailability } = menuSlice.actions;

/* -------------------------------- selectors ------------------------------- */

export const selectMenu = (state) => state.menu;
export const selectCategories = (state) => state.menu.categories;
export const selectLookups = (state) => state.menu.lookups;
export const selectSettings = (state) => state.menu.settings;
export const selectBusiness = (state) => state.menu.business;

/**
 * The tiles to show, given the active category and the search box.
 *
 * Memoised because it runs on every keystroke over the whole menu. Sold-out
 * items are kept in the list rather than filtered out - a cashier needs to see
 * that the thing a customer just asked for exists but is off, not wonder where
 * it went.
 */
export const selectVisibleItems = createSelector(
    [(state) => state.menu.items, (state) => state.menu.activeCategoryId, (state) => state.menu.search],
    (items, activeCategoryId, search) => {
        const term = search.trim().toLowerCase();

        return items.filter((item) => {
            if (activeCategoryId && item.categoryId !== activeCategoryId) return false;
            if (!term) return true;

            return (
                item.name.toLowerCase().includes(term)
                || (item.code || '').toLowerCase().includes(term)
                || (item.barcode || '').toLowerCase() === term
                || (item.nameUrdu || '').includes(search.trim())
            );
        });
    }
);

/**
 * The item a scanned or typed code belongs to, from the menu in memory.
 *
 * A size's own barcode is the most specific match - a 500ml and a 1.5L bottle
 * of the same drink carry different codes - so it wins, then the item's
 * barcode, then its short code. `variant` is null when the code is the item's.
 */
export const findItemByCode = (items, code) => {
    const term = String(code ?? '').trim().toLowerCase();
    if (!term) return null;
    const matches = (value) => (value || '').toLowerCase() === term;

    for (const item of items) {
        const variant = item.variants?.find((v) => matches(v.barcode));
        if (variant) return { item, variant };
    }

    const item = items.find((i) => matches(i.barcode)) || items.find((i) => matches(i.code));
    return item ? { item, variant: null } : null;
};

/** Payment modes in display order - the Cash / Card / Wallet strip. */
export const selectPaymentModes = (state) => state.menu.lookups.paymentModes;

/** Order types - the Dine In / Take Away / Delivery chips. */
export const selectOrderTypes = (state) => state.menu.lookups.orderTypes;

/** One typed setting, with a fallback. */
export const selectSetting = (name, fallback = null) => (state) => {
    const value = state.menu.settings?.[name];
    return value === undefined ? fallback : value;
};

export default menuSlice.reducer;
